/**
 * The pluggable video encoder. The key to interchangeability is that the SINK
 * OWNS THE CANVAS: the caller draws each frame into `sink.canvas` and calls
 * `addFrame()`. Neither the caller nor the compositor knows which backend
 * (WebCodecs or MediaRecorder) is underneath.
 */

import type { VideoEncoderBackend, VideoExportSettings } from '@canvas/shared-types';
import { planEncoder, type EncoderPlan } from './encoderCapabilities';
import { abortError } from './exportErrors';
import { createMediaRecorderSink } from './mediaRecorderEncoder';

export interface EncodedVideoResult {
  blob: Blob;
  mimeType: string;
  width: number;
  height: number;
  fps: number;
  durationMs: number;
  backend: VideoEncoderBackend;
  /** User-facing note when the plan was downgraded. */
  note?: string;
}

export interface VideoEncoderSink {
  readonly backend: VideoEncoderBackend;
  /** Sink-owned target. The caller draws every frame into this. */
  readonly canvas: HTMLCanvasElement;
  /** true => the sink needs wall-clock pacing (MediaRecorder). */
  readonly realtime: boolean;
  /** Call after drawing frame `index`. Awaits encoder backpressure. */
  addFrame(index: number, timestampMicros: number, keyFrame: boolean): Promise<void>;
  /** WebCodecs path: PCM for the audio track. The sink closes `data`. */
  addAudio?(data: AudioData): Promise<void>;
  /**
   * MediaRecorder path: attach a live audio track instead. `onStart` fires
   * the moment recording begins, so the caller can start playback in sync.
   * Must be called before the first addFrame.
   */
  attachAudioTrack?(track: MediaStreamTrack, onStart?: () => void): void;
  finalize(): Promise<EncodedVideoResult>;
  /** Never finalizes. Tears everything down. */
  abort(reason?: string): Promise<void>;
}

export interface CreateSinkOptions {
  settings: VideoExportSettings;
  durationMs: number;
  signal?: AbortSignal;
  /** Reuse a plan the caller already computed (and showed to the user). */
  plan?: EncoderPlan;
  /** Present when the export has a mixed audio track (WebCodecs muxes it). */
  audio?: { sampleRate: number; numberOfChannels: number };
  /**
   * Large MP4s are written here as they encode, instead of being held in
   * memory. Ignored by the WebM and MediaRecorder paths.
   */
  fileHandle?: FileSystemFileHandle;
}

/** Build the sink for `settings`. The WebCodecs backend loads on demand to keep the muxers out of the editor chunk. */
export async function createVideoEncoderSink(opts: CreateSinkOptions): Promise<VideoEncoderSink> {
  const plan = opts.plan ?? (await planEncoder(opts.settings));
  if (opts.signal?.aborted) throw abortError();
  if (plan.backend === 'webcodecs') {
    const { createWebCodecsSink } = await import('./webcodecsEncoder');
    return createWebCodecsSink({ ...opts, plan });
  }
  return createMediaRecorderSink({ ...opts, plan });
}
